import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ItemClassModel } from '../model/item-class.model';
import { ItemClassService } from '../item-class.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { PermissionService } from 'src/app/auth/permission.service';
import { DepartmentService } from '../../../system/department/department.service';
import { Observable, map, startWith } from 'rxjs';

@Component({
  selector: 'app-item-class-edit',
  templateUrl: './item-class-edit.component.html',
  styleUrls: ['./item-class-edit.component.scss'],
  standalone: false
})
export class ItemClassEditComponent implements DirtyCheck {
  title: string = "New Item Class";
  editForm!: FormGroup;
  itemClassInfo: ItemClassModel = {
    id: 0,
    code: '',
    name: '',
    department_id: 0,
    description: '',
    super_class_id: null,
    created_by: 0,
    created_at: '',
    updated_by: null,
    superClassName: null,
    department: '',
    publish_status: 1
  };
  isFormDirty: boolean = false;
  context: any;
  departments: any[] = [];
  selectedDepartments: any[] = [];
  superClasses: any[] = [];
  permission: any;
  
  // Autocomplete controls
  departmentControl = new FormControl();
  superClassControl = new FormControl();
  filteredDepartments!: Observable<any[]>;
  filteredSuperClasses!: Observable<any[]>;
  
  // Form state
  isSubclass: boolean = false;
  selectedDepartment: any = null;
  selectedSuperClass: any = null;
  
  // Type options for dropdown
  typeOptions = [
    { value: 'superclass', label: 'Super Class' },
    { value: 'subclass', label: 'Subclass' }
  ];
  selectedType: string = 'superclass';

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private itemClassService: ItemClassService,
    private departmentService: DepartmentService,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService,
    public dialog: MatDialog
  ) {
    let itemClassId = this.route.snapshot.paramMap.get('id');
    this.permission = this.permissionService.getPagePermission('item-class');

    if (itemClassId !== null && parseInt(itemClassId) !== 0) {
      this.setItemClassInfo(itemClassId);
    } else {
      // For new items, initialize with empty form
      this.title = "New Item Class";
      this.initForm();
    }
  }

  ngOnInit(): void {
    this.loadDepartmentList();
    this.loadSuperClassList();
  }

  loadDepartmentList(): void {
    let requestData = {
      search: {
        filters: {
          column: [],
          scope: { limit: 100, offset: 0 },
        },
        sort: [{ column: "code", order: "asc" }],
      },
    };

    this.departmentService.getDepartmentList(requestData).subscribe({
      next: (response: any) => {
        if (response.status === "SUCCESS") {
          this.departments = response.data.departments || [];
          this.setupDepartmentAutocomplete();
          this.updateFormWithLoadedData();
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: "Failed to load departments!!!",
            type: "error",
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error: any) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to load departments. Connection to the server failed',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  loadSuperClassList(): void {
    let requestData = {
      search: {
        filters: {
          column: [],
          scope: { limit: 100, offset: 0 },
        },
        sort: [{ column: "name", order: "asc" }],
      },
    };

    this.itemClassService.getParentClass(requestData).subscribe({
      next: (response: any) => {
        if (response.status === "SUCCESS") {
          this.superClasses = response.data.super_classes || response.data.item_classes || response.data || [];
          this.setupSuperClassAutocomplete();
          this.updateFormWithLoadedData();
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: "Failed to load super classes!!!",
            type: "error",
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error: any) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to load super classes. Connection to the server failed',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  setupDepartmentAutocomplete(): void {
    if (!this.departmentControl) {
      this.departmentControl = new FormControl();
    }
    this.filteredDepartments = this.departmentControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterDepartments(value || ''))
    );
    
    // Mark form as dirty when department control changes
    this.departmentControl.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });
  }

  setupSuperClassAutocomplete(): void {
    if (!this.superClassControl) {
      this.superClassControl = new FormControl();
    }
    this.filteredSuperClasses = this.superClassControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterSuperClasses(value || ''))
    );
    
    // Mark form as dirty when superclass control changes
    this.superClassControl.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });
  }

  private _filterDepartments(value: string): any[] {
    if (!this.departments || this.departments.length === 0) {
      return [];
    }
    const filterValue = value.toLowerCase();
    return this.departments.filter(dept => 
      dept.name.toLowerCase().includes(filterValue) ||
      dept.code.toLowerCase().includes(filterValue)
    );
  }

  private _filterSuperClasses(value: string): any[] {
    if (!this.superClasses || this.superClasses.length === 0) {
      return [];
    }
    const filterValue = value.toLowerCase();
    return this.superClasses.filter(superClass => 
      (superClass.name || superClass.className || '').toLowerCase().includes(filterValue) ||
      (superClass.code || '').toLowerCase().includes(filterValue)
    );
  }

  initForm() {
    // Determine if this is a subclass
    this.isSubclass = !!this.itemClassInfo.super_class_id;
    
    // Set up department and super class selections (only if data is loaded)
    if (this.itemClassInfo.department_id && this.departments && this.departments.length > 0) {
      this.selectedDepartment = this.departments.find(dept => dept.id === this.itemClassInfo.department_id);
      if (this.selectedDepartment) {
        this.departmentControl.setValue(this.selectedDepartment.name);
      }
    }
    
    if (this.itemClassInfo.super_class_id && this.superClasses && this.superClasses.length > 0) {
      this.selectedSuperClass = this.superClasses.find(sc => sc.id === this.itemClassInfo.super_class_id);
      if (this.selectedSuperClass) {
        this.superClassControl.setValue(this.selectedSuperClass.name || this.selectedSuperClass.className);
      }
    }

    this.selectedType = this.isSubclass ? 'subclass' : 'superclass';
    
    this.editForm = this.builder.group({
      code: this.builder.control(this.itemClassInfo.code, [Validators.required, Validators.maxLength(10)]),
      name: this.builder.control(this.itemClassInfo.name, [Validators.required, Validators.maxLength(50)]),
      alternative_name: this.builder.control(this.itemClassInfo.alternative_name),
      description: this.builder.control(this.itemClassInfo.description),
      department_id: this.builder.control(this.itemClassInfo.department_id, [this.departmentValidator]),
      super_class_id: this.builder.control(this.itemClassInfo.super_class_id, [this.superClassValidator]),
      type: this.builder.control(this.selectedType),
      bg_color: this.builder.control(this.itemClassInfo.bg_color || '#A9A9A9'),
      fg_color: this.builder.control(this.itemClassInfo.fg_color || '#FFFFFF'),
      apply_colors: this.builder.control(!!Number(this.itemClassInfo.can_apply_colors)),
      account_code: this.builder.control(this.itemClassInfo.account_code),
      is_active: this.builder.control(Number(this.itemClassInfo.publish_status) || 1)
    });
    
    // Watch for type dropdown changes
    this.editForm.get('type')?.valueChanges.subscribe(type => {
      this.selectedType = type;
      this.isSubclass = type === 'subclass';
      
      if (type === 'superclass') {
        // Clear super class when switching to superclass
        this.editForm.get('super_class_id')?.setValue(null);
        this.selectedSuperClass = null;
        this.superClassControl.setValue('');
        
        // Enable department control for superclass
        this.departmentControl.enable();
      } else if (type === 'subclass') {
        // Clear department when switching to subclass (will be inherited from super class)
        this.editForm.get('department_id')?.setValue(null);
        this.selectedDepartment = null;
        this.departmentControl.setValue('');
        
        // Disable department control for subclass
        this.departmentControl.disable();
      }
      
      // Revalidate the department and superclass fields after type change
      this.editForm.get('department_id')?.updateValueAndValidity();
      this.editForm.get('super_class_id')?.updateValueAndValidity();
    });
    
    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });
    
    // Set initial department control state based on type
    if (this.isSubclass) {
      this.departmentControl.disable();
    } else {
      this.departmentControl.enable();
    }
  }

  /**
   * Custom validator for department field
   */
  departmentValidator = (control: any) => {
    if (this.selectedType === 'superclass' && !this.selectedDepartment) {
      return { departmentRequired: true };
    }
    return null;
  }

  /**
   * Custom validator for superclass field
   */
  superClassValidator = (control: any) => {
    if (this.selectedType === 'subclass' && !this.selectedSuperClass) {
      return { superClassRequired: true };
    }
    return null;
  }

  /**
   * Dirtyness check
   * @returns 
   */
  isDirty(): boolean {
    return this.isFormDirty;
  }

  /**
   * checks the form is edited
   */
  navBack(): void {
    this.router.navigate(['/product/item-class']);
  }

  /**
   * If id is null set an empty item class info
   * if item class id is not null call api to retrieve the info
   * @param itemClassId 
   */
  setItemClassInfo(itemClassId: any): void {
    let response = this.itemClassService.getById(itemClassId).subscribe({
      next: (response: any) => {
        if (response.status == 'SUCCESS') {
          this.itemClassInfo = response.data.item_class;
          // Load departments and superclasses if provided in response
          if (response.data.departments) {
            this.departments = response.data.departments;
            this.setupDepartmentAutocomplete();
          }
          if (response.data.super_classes) {
            this.superClasses = response.data.super_classes;
            this.setupSuperClassAutocomplete();
          }
          this.title = this.getTitle();
          this.initForm();
          this.isFormDirty = false;
          
          // Update form with loaded data after a short delay to ensure everything is initialized
          setTimeout(() => {
            this.updateFormWithLoadedData();
          }, 100);
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error: any) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get data from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  /**
   * @returns the title for the dialog
   * since the same dialog is used for edit and new, dynamically decide the title
   */
  getTitle(): string {
    let title = (this.itemClassInfo.id == 0 || this.itemClassInfo.id == undefined) ? "New Item Class" : "Editing : " + this.itemClassInfo.name;
    return title;
  }

  onDepartmentCheckboxChange(event: any, department: any, index: number) {
    if (event.checked) {
      // Add if not already present
      if (!this.selectedDepartments.find(dep => dep.id === department.id)) {
        this.selectedDepartments.push(department);
      }
    } else {
      // Remove unchecked
      this.selectedDepartments = this.selectedDepartments.filter(dep => dep.id !== department.id);
    }
  }

  onTypeChange(type: string): void {
    this.selectedType = type;
    this.isSubclass = type === 'subclass';
    this.editForm.get('type')?.setValue(type);
    this.isFormDirty = true; // Mark form as dirty when type changes
    
    if (type === 'superclass') {
      // Clear super class when switching to superclass
      this.editForm.get('super_class_id')?.setValue(null);
      this.selectedSuperClass = null;
      this.superClassControl.setValue('');
      
      // Enable department control for superclass
      this.departmentControl.enable();
      
      // Mark department as touched to show validation if empty
      this.departmentControl.markAsTouched();
      this.editForm.get('department_id')?.markAsTouched();
    } else if (type === 'subclass') {
      // Clear department when switching to subclass (will be inherited from super class)
      this.editForm.get('department_id')?.setValue(null);
      this.selectedDepartment = null;
      this.departmentControl.setValue('');
      
      // Disable department control for subclass
      this.departmentControl.disable();
      
      // Mark superclass as touched to show validation if empty
      this.superClassControl.markAsTouched();
      this.editForm.get('super_class_id')?.markAsTouched();
    }
    
    // Revalidate the department and superclass fields after type change
    this.editForm.get('department_id')?.updateValueAndValidity();
    this.editForm.get('super_class_id')?.updateValueAndValidity();
  }

  onDepartmentSelected(department: any): void {
    this.selectedDepartment = department;
    this.editForm.get('department_id')?.setValue(department.id);
    this.departmentControl.setValue(department.name);
    this.isFormDirty = true; // Mark form as dirty when department is selected
    // Revalidate after selection
    this.editForm.get('department_id')?.updateValueAndValidity();
  }

  onSuperClassSelected(superClass: any): void {
    this.selectedSuperClass = superClass;
    this.editForm.get('super_class_id')?.setValue(superClass.id);
    this.superClassControl.setValue(superClass.name || superClass.className);
    this.isFormDirty = true; // Mark form as dirty when superclass is selected
    
    // STEP 2 & 3: Automatic Department Inheritance
    // When a superclass is selected, immediately inherit its department
    this.inheritDepartmentFromSuperClass(superClass);
    
    // Revalidate after selection
    this.editForm.get('super_class_id')?.updateValueAndValidity();
  }

  /**
   * Inherits department from selected superclass
   * Business Rule: Subclasses must always be in the same department as their parent
   */
  inheritDepartmentFromSuperClass(superClass: any): void {
    if (superClass && superClass.department_id) {
      // Look up the department from our loaded departments list
      const inheritedDepartment = this.departments.find(dept => dept.id === superClass.department_id);
      
      if (inheritedDepartment) {
        // Set the inherited department as selected
        this.selectedDepartment = inheritedDepartment;
        
        // Store the department ID in the form for saving
        this.editForm.get('department_id')?.setValue(inheritedDepartment.id);
        
        // Update the department control to show inherited department
        this.departmentControl.setValue(inheritedDepartment.name);
        
        this.isFormDirty = true; // Mark form as dirty when department is inherited
        
        console.log(`Department inherited: ${inheritedDepartment.name} (ID: ${inheritedDepartment.id}) from superclass: ${superClass.name || superClass.className}`);
      } else {
        console.warn(`Could not find department with ID: ${superClass.department_id} for superclass: ${superClass.name || superClass.className}`);
        // Clear department if we can't find it
        this.selectedDepartment = null;
        this.editForm.get('department_id')?.setValue(null);
        this.departmentControl.setValue('');
      }
    } else {
      console.warn('Superclass does not have a department_id:', superClass);
      // Clear department if superclass has no department
      this.selectedDepartment = null;
      this.editForm.get('department_id')?.setValue(null);
      this.departmentControl.setValue('');
    }
  }

  displayDepartment(department: any): string {
    if (typeof department === 'string') {
      return department;
    }
    return department && department.name ? department.name : '';
  }

  displaySuperClass(superClass: any): string {
    if (typeof superClass === 'string') {
      return superClass;
    }
    return superClass && (superClass.name || superClass.className) ? 
           (superClass.name || superClass.className) : '';
  }

  getDepartmentDisplayText(): string {
    // For subclasses, show inherited department
    if (this.isSubclass) {
      if (this.selectedDepartment) {
        return this.selectedDepartment.name;
      }
      
      // Fallback: Try to find department from selected superclass
      if (this.selectedSuperClass && this.selectedSuperClass.department_id) {
        const department = this.departments.find(d => d.id === this.selectedSuperClass.department_id);
        return department ? department.name : 'Department not found';
      }
      
      return 'Select Super Class first';
    }
    
    // For parent classes, show selected department
    return this.selectedDepartment ? this.selectedDepartment.name : '';
  }

  clearDepartment(): void {
    this.selectedDepartment = null;
    this.departmentControl.setValue('');
    this.departmentControl.markAsTouched();
    this.editForm.get('department_id')?.setValue(null);
    this.editForm.get('department_id')?.markAsTouched();
    this.editForm.get('department_id')?.updateValueAndValidity();
    this.isFormDirty = true; // Mark form as dirty when department is cleared
  }

  clearSuperClass(): void {
    this.selectedSuperClass = null;
    this.superClassControl.setValue('');
    this.superClassControl.markAsTouched();
    this.editForm.get('super_class_id')?.setValue(null);
    this.isFormDirty = true; // Mark form as dirty when superclass is cleared
    
    // Also clear inherited department when superclass is cleared
    this.selectedDepartment = null;
    this.editForm.get('department_id')?.setValue(null);
    this.departmentControl.setValue('');
    
    // Mark fields as touched and revalidate
    this.editForm.get('super_class_id')?.markAsTouched();
    this.editForm.get('super_class_id')?.updateValueAndValidity();
    
    console.log('Super class cleared, inherited department also cleared');
  }

  updateFormWithLoadedData(): void {
    // Re-initialize form with loaded data after departments and superclasses are loaded
    if (this.itemClassInfo && this.editForm) {
      
      // Set up super class selection first (for subclasses)
      if (this.itemClassInfo.super_class_id && this.superClasses && this.superClasses.length > 0) {
        this.selectedSuperClass = this.superClasses.find(sc => sc.id === this.itemClassInfo.super_class_id);
        if (this.selectedSuperClass) {
          this.superClassControl.setValue(this.selectedSuperClass.name || this.selectedSuperClass.className);
          
          // Inherit department from superclass
          this.inheritDepartmentFromSuperClass(this.selectedSuperClass);
          
          // Disable department control for subclass
          this.departmentControl.disable();
        }
      }
      // For parent classes, set department directly
      else if (!this.isSubclass && this.itemClassInfo.department_id && this.departments && this.departments.length > 0) {
        this.selectedDepartment = this.departments.find(dept => dept.id === this.itemClassInfo.department_id);
        if (this.selectedDepartment) {
          this.departmentControl.setValue(this.selectedDepartment.name);
        }
        
        // Enable department control for superclass
        this.departmentControl.enable();
      }
      
      // Reset dirty state after loading initial data
      this.isFormDirty = false;
    }
  }
  /**
   * On save
   * if the details are valid call the api to update the changes.
   */
  save(): void {
    // Mark all fields as touched to show validation errors
    this.editForm.markAllAsTouched();
    
    // Revalidate custom validators
    this.editForm.get('department_id')?.updateValueAndValidity();
    this.editForm.get('super_class_id')?.updateValueAndValidity();
    
    // Check for custom validation errors
    let hasValidationErrors = false;
    let errorMessage = '';
    
    if (this.selectedType === 'superclass' && !this.selectedDepartment) {
      hasValidationErrors = true;
      errorMessage = 'Please select department for superclass';
    }
    
    if (this.selectedType === 'subclass' && !this.selectedSuperClass) {
      hasValidationErrors = true;
      errorMessage = 'Please select superclass for subclass';
    }
    
    if (hasValidationErrors) {
      let snackBarData: SnackBarData = {
        message: errorMessage,
        title: 'Validation Error',
        type: 'error'
      };
      this.snackBarService.openSnackBar(snackBarData);
      return;
    }
    
    if (this.editForm.valid) {
      
      // Prepare the data for API submission
      const formData = { ...this.editForm.value };
      
      // Ensure department_id is set correctly
      if (this.isSubclass && this.selectedSuperClass) {
        // For subclasses, use department from super class if not explicitly set
        if (!formData.department_id && this.selectedSuperClass.department_id) {
          formData.department_id = this.selectedSuperClass.department_id;
        }
      } else if (!this.isSubclass && this.selectedDepartment) {
        // For parent classes, use selected department
        formData.department_id = this.selectedDepartment.id;
      }
      
      // Clear super_class_id if not a subclass
      if (!this.isSubclass) {
        formData.super_class_id = null;
        formData.bg_color = null;
        formData.fg_color = null;
        formData.apply_colors = false;
      }
      
      // Map apply_colors to can_apply_colors for API compatibility
      if (formData.apply_colors !== undefined) {
        formData.can_apply_colors = formData.apply_colors ? 1 : 0;
        delete formData.apply_colors;
      }
      
      // Remove type as it's not needed in API
      delete formData.type;
      
      Object.assign(this.itemClassInfo, formData);
      
      this.itemClassService.update(this.itemClassInfo).subscribe({
        next: (response: any) => {
          if (response.status == 'SUCCESS') {
            this.isFormDirty = false;
            let snckBarData: SnackBarData = {
              type: 'success',
              title: 'Saved',
              message: 'The record for ' + this.itemClassInfo.name + " has been updated successfully."
            };
            this.snackBarService.openSnackBar(snckBarData);
            this.router.navigate(['/product/item-class']);
          } else {
            let snackBarData: SnackBarData = {
              message: response.message,
              title: 'Failed!!!',
              type: 'error'
            };
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: (error: any) => {
          let snackBarData: SnackBarData = {
            message: 'Failed to update. Connection to the server failed',
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      });
    } else {
      this.editForm.markAllAsTouched();
    }
  }
}